import { Injectable } from '@angular/core';
import { FeatureFlagsService } from './featureFlags.service';
import { DocumentService } from './documet/document.service';
import { NotificationService } from './notification.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';


export interface FacilityDocumentStatus {
  expiredLicense: boolean;
  expiredMessage: string;
  state_license_missing: boolean;
  state_license_expired: boolean;
  dea_license_missing: boolean;
  dea_license_expired: boolean;
}

@Injectable({
  providedIn: 'root',
})

export class FacilityDocumentsService {
  emptyStatus: FacilityDocumentStatus = {
    expiredLicense: false,
    expiredMessage: '',
    state_license_missing: false,
    state_license_expired: false,
    dea_license_missing: false,
    dea_license_expired: false,
  };  
  constructor(private featureFlagsService: FeatureFlagsService, private documentService: DocumentService, private notificationService: NotificationService) { }

  _getStatus(facility: any): Observable<FacilityDocumentStatus> {
    if (!facility) {
      return of(this.emptyStatus);
    }
    const search = {
      context: 'facility',
      facility_id: facility.id,
    }
    return this.documentService.get(search).pipe(
      map((documents) => this.evaluateStatus(facility, documents.data)),
      catchError((error) => {
        console.error('Error fetching documents:', error);
        this.notificationService.showErrorMessage('Error fetching documents');
        return of(this.emptyStatus);
      }),
    );
  }

  async getStatus(facility: any): Promise<FacilityDocumentStatus> {
    try {
      return await this._getStatus(facility).toPromise();
    } catch (error) {
      console.error('Error fetching documents:', error);
      this.notificationService.showErrorMessage('Error fetching documents');
      return this.emptyStatus;
    }
  }

  evaluateStatus(facility, documents, skipDocumentCheck = false): FacilityDocumentStatus {
    const status: FacilityDocumentStatus = { ...this.emptyStatus };

    if (!facility.state_license?.length) {
      status.state_license_missing = true;
    }

    const stateLicenseDoc = documents?.find(
      (doc: { type?: string }) => doc?.type === 'state_license',
    );

    const deaLicenseDoc = documents?.find(
      (doc: { type?: string }) => doc?.type === 'dea_license',
    );

    if (!skipDocumentCheck && !stateLicenseDoc) {
      status.state_license_missing = true;
    }

    if (!status.state_license_missing && facility.state_license_expired) {
      status.state_license_expired = true;
    }

    if (this.featureFlagsService.get('warn_on_expired_dea') && !facility.dea_number?.length) {
      status.dea_license_missing = true;
    }

    if (this.featureFlagsService.get('warn_on_expired_dea') && !skipDocumentCheck && !deaLicenseDoc) {
      status.dea_license_missing = true;
    }

    if (
      this.featureFlagsService.get('warn_on_expired_dea') &&
      facility.dea_license_expired
    ) {
      status.dea_license_expired = true;
    }

    status.expiredLicense =
      status.state_license_missing ||
      status.state_license_expired ||
      status.dea_license_missing ||
      status.dea_license_expired;

    if (status.state_license_missing && status.dea_license_missing) {
      status.expiredMessage =
        "This facility's state and DEA licenses are missing.";
    } else if (status.state_license_expired && status.dea_license_missing) {
      status.expiredMessage =
        "This facility's state license is expired and DEA license is missing.";
    } else if (status.state_license_missing && status.dea_license_expired) {
      status.expiredMessage =
        "This facility's state license is missing and DEA license is expired.";
    } else if (status.state_license_expired && status.dea_license_expired) {
      status.expiredMessage =
        "This facility's state and DEA licenses are expired.";
    } else if (status.state_license_missing) {
      status.expiredMessage = "This facility's state license is missing.";
    } else if (status.state_license_expired) {
      status.expiredMessage = "This facility's state license is expired.";
    } else if (status.dea_license_missing) {
      status.expiredMessage = "This facility's DEA license is missing.";
    } else if (status.dea_license_expired) {
      status.expiredMessage = "This facility's DEA license is expired.";
    }
    return status;
  }

  isBlocking(
    status: FacilityDocumentStatus,
    cartHasControlledSubstance: boolean,
  ): boolean {
    const hasStateIssue =
      status.state_license_missing || status.state_license_expired;
    const hasDeaIssue = status.dea_license_missing || status.dea_license_expired;

    return hasStateIssue || (cartHasControlledSubstance && hasDeaIssue);
  }
}
